"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sparkles, BookOpen, ArrowRight } from "lucide-react";
const rareNames = [{
  id: "n1",
  name: "Aurelia",
  origin: "Latin",
  meaning: "The golden one",
  rarityScore: "9.2/10",
  description: "A luminous and sophisticated name with roots in ancient Rome. It carries a sense of timeless elegance and warmth, perfect for a child who brings light into your life."
}, {
  id: "n2",
  name: "Cassian",
  origin: "Latin",
  meaning: "Hollow; empty",
  rarityScore: "8.7/10",
  description: "Despite its meaning, Cassian feels full of life and literary charm. It has a romantic, slightly rugged appeal that stands out from more common traditional boy names."
}, {
  id: "n3",
  name: "Elara",
  origin: "Greek",
  meaning: "Shining; one of Jupiter's moons",
  rarityScore: "9.5/10",
  description: "Celestial and delicate, Elara is a mythological name that feels both ancient and futuristic. It offers a beautiful alternative to popular names like Ella or Clara."
}, {
  id: "n4",
  name: "Leander",
  origin: "Greek",
  meaning: "Lion-man",
  rarityScore: "8.9/10",
  description: "A heroic name from Greek mythology, Leander is strong yet poetic. It provides a softer, more melodic alternative to Alexander or Evander."
}, {
  id: "n5",
  name: "Seraphina",
  origin: "Hebrew",
  meaning: "Ardent; fiery",
  rarityScore: "9.1/10",
  description: "Derived from the highest order of angels, Seraphina is an ethereal, elaborate name. It is dramatic, beautiful, and carries a sense of passionate energy."
}, {
  id: "n6",
  name: "Thaddeus",
  origin: "Aramaic",
  meaning: "Heart; courageous heart",
  rarityScore: "8.4/10",
  description: "A strong, biblical name that has flown under the radar for decades. Thaddeus feels distinguished and offers the friendly, approachable nickname Thad."
}, {
  id: "n7",
  name: "Isolde",
  origin: "Welsh",
  meaning: "Ice ruler",
  rarityScore: "9.6/10",
  description: "Steeped in Arthurian legend and romantic tragedy, Isolde is a name of profound depth and mystery. It is striking, rare, and unforgettable."
}, {
  id: "n8",
  name: "Orion",
  origin: "Greek",
  meaning: "Rising in the sky; dawning",
  rarityScore: "8.5/10",
  description: "A prominent constellation name that feels adventurous and bold. Orion is recognizable but remains an uncommon, striking choice for a modern boy."
}, {
  id: "n9",
  name: "Zephyr",
  origin: "Greek",
  meaning: "West wind",
  rarityScore: "9.8/10",
  description: "Light, breezy, and incredibly rare. Zephyr is a mythological name that feels fresh and energetic, perfect for parents seeking something truly distinctive."
}];
export default function TheRareNameDirectory() {
  return <section id="the-rare-name-directory" className="py-20 bg-background text-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        
        {}
        <div className="mb-16 max-w-3xl">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-6 h-6 text-secondary" />
            <span className="text-secondary font-semibold tracking-wider uppercase text-sm">Discovery Collection</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6 leading-tight">
            The Rare Name Directory
          </h2>
          <p className="text-lg text-foreground/80 leading-relaxed">
            Explore our curated selection of uncommon and distinctive names. 
            Each name is carefully researched, complete with its origin, deep meaning, 
            and a unique rarity score to help you find a truly one-of-a-kind choice.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rareNames.map((item, i) => {
          const isAlternateRow = Math.floor(i / 3) % 2 !== 0;
          const cardBgClass = isAlternateRow ? "bg-card/30" : "bg-background";
          return <Card key={item.id} data-index={i} className={`rounded-none border border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col h-full ${cardBgClass}`}>
                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-3xl font-bold text-primary">
                      {item.name}
                    </CardTitle>
                    <Badge variant="outline" className="rounded-none border-primary/20 bg-primary/5 text-primary font-semibold">
                      Score: {item.rarityScore}
                    </Badge>
                  </div>
                  <CardDescription className="flex items-center gap-2 text-foreground/70 text-sm font-medium uppercase tracking-wide">
                    <BookOpen className="w-4 h-4" />
                    {item.origin} Origin
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="flex-grow">
                  <div className="mb-4">
                    <span className="text-xs text-foreground/60 uppercase tracking-wider font-semibold block mb-1">Meaning</span>
                    <p className="text-lg font-medium text-foreground">"{item.meaning}"</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground/60 uppercase tracking-wider font-semibold block mb-1">The Story</span>
                    <p className="text-foreground/80 leading-relaxed text-sm">
                      {item.description}
                    </p>
                  </div>
                </CardContent>

                <CardFooter className="pt-4 border-t border-border/30 mt-auto">
                  <Button variant="ghost" className="w-full justify-between rounded-none text-secondary hover:text-accent-foreground hover:bg-accent/20 group" asChild>
                    <Link to={`/UniqueNamesPage#${item.name.toLowerCase()}`}>
                      View Full Details
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>;
        })}
        </div>

        {}
        <div className="mt-16 text-center">
          <Button size="lg" className="rounded-none bg-secondary text-secondary-foreground hover:bg-accent hover:text-accent-foreground px-8 py-6 text-lg font-semibold transition-colors duration-300" asChild>
            <Link to="/UniqueNamesPage">
              Explore All Unique Names
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}